import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useClientAuth } from "@/lib/clientAuth";
import GoogleSignInButton from "@/components/site/GoogleSignInButton";
import { UserPlus, ArrowRight, User, Building2 } from "lucide-react";

const businessTypes = ["Retailer", "Distributor", "Manufacturer", "E-commerce", "Wholesaler", "Contractor", "Other"];
const states = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

const initial = {
  name: "", email: "", password: "", phone: "", job_title: "", preferred_contact: "email",
  company_name: "", business_type: "Retailer",
  company_address: "", city: "", state: "UT", zip_code: "", ein: "", website: "",
};

export default function ClientRegister() {
  const { register } = useClientAuth();
  const nav = useNavigate();
  const [form, setForm] = useState(initial);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.password.length < 6) { setErr("Password must be at least 6 characters."); return; }
    setLoading(true);
    try {
      await register(form);
      nav("/portal");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : "Registration failed. Please check your info and try again.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="client-register-page" className="min-h-screen pt-24 pb-24 bg-brand-bg">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-xs tracking-[0.3em] uppercase font-bold text-primary-brand mb-3">Client Portal</div>
          <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-slate-900">
            Create your account.
          </h1>
          <p className="mt-3 text-slate-600 max-w-2xl">
            Once registered, you can request quotes, track your shipments, and manage your account in one place.
          </p>
        </motion.div>

        <form onSubmit={submit} data-testid="client-register-form" className="mt-10 space-y-8">
          <div className="border border-slate-200 bg-white p-6 lg:p-8">
            <div className="max-w-md mx-auto">
              <GoogleSignInButton testid="client-register-google" label="Sign up with Google" />
              <div className="my-5 flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] font-bold text-slate-400">
                <span className="h-px flex-1 bg-slate-200" />
                or fill the form below
                <span className="h-px flex-1 bg-slate-200" />
              </div>
              <p className="text-xs text-slate-500 text-center">
                Google sign-up gets you inside instantly — you can complete company info from your dashboard.
              </p>
            </div>
          </div>

          {/* Personal */}
          <div className="border border-slate-200 bg-white">
            <div className="px-6 lg:px-8 py-4 border-b border-slate-200 flex items-center gap-2">
              <User className="h-4 w-4 text-primary-brand" />
              <div className="text-xs uppercase tracking-[0.25em] font-bold text-slate-500">Personal Information</div>
            </div>
            <div className="p-6 lg:p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Full name *"><Input required value={form.name} onChange={upd("name")} testid="reg-name" /></Field>
              <Field label="Email *"><Input type="email" required value={form.email} onChange={upd("email")} testid="reg-email" /></Field>
              <Field label="Password *"><Input type="password" required value={form.password} onChange={upd("password")} testid="reg-password" /></Field>
              <Field label="Phone *"><Input required value={form.phone} onChange={upd("phone")} testid="reg-phone" /></Field>
              <Field label="Job title"><Input value={form.job_title} onChange={upd("job_title")} testid="reg-job" /></Field>
              <Field label="Preferred contact">
                <Select value={form.preferred_contact} onChange={upd("preferred_contact")} testid="reg-contact">
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="sms">SMS</option>
                </Select>
              </Field>
            </div>
          </div>

          {/* Company */}
          <div className="border border-slate-200 bg-white">
            <div className="px-6 lg:px-8 py-4 border-b border-slate-200 flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary-brand" />
              <div className="text-xs uppercase tracking-[0.25em] font-bold text-slate-500">Company Information</div>
            </div>
            <div className="p-6 lg:p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Company name *"><Input required value={form.company_name} onChange={upd("company_name")} testid="reg-company" /></Field>
              <Field label="Business type">
                <Select value={form.business_type} onChange={upd("business_type")} testid="reg-btype">
                  {businessTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                </Select>
              </Field>
              <Field label="Company address" full><Input value={form.company_address} onChange={upd("company_address")} testid="reg-addr" /></Field>
              <Field label="City"><Input value={form.city} onChange={upd("city")} testid="reg-city" /></Field>
              <Field label="State">
                <Select value={form.state} onChange={upd("state")} testid="reg-state">
                  {states.map((s) => <option key={s} value={s}>{s}</option>)}
                </Select>
              </Field>
              <Field label="ZIP code"><Input value={form.zip_code} onChange={upd("zip_code")} testid="reg-zip" /></Field>
              <Field label="EIN / Tax ID"><Input value={form.ein} onChange={upd("ein")} testid="reg-ein" /></Field>
              <Field label="Website" full><Input value={form.website} onChange={upd("website")} placeholder="https://" testid="reg-website" /></Field>
            </div>
          </div>

          {err && (
            <div data-testid="client-register-error" className="border border-red-300 bg-red-50 text-red-700 px-4 py-3 text-sm">{err}</div>
          )}

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border border-slate-200 bg-white p-6">
            <div className="text-sm text-slate-600">
              Already have an account?{" "}
              <Link to="/portal/login" data-testid="reg-signin-link" className="text-primary-brand font-bold hover:underline">Sign in</Link>
            </div>
            <button
              type="submit" disabled={loading}
              data-testid="client-register-submit"
              className="inline-flex items-center gap-2 bg-primary-brand hover:bg-[#E04F00] disabled:opacity-60 text-white px-8 py-4 font-bold uppercase tracking-wider text-sm"
            >
              <UserPlus className="h-4 w-4" />
              {loading ? "Creating…" : "Create Account"}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-2">{label}</div>
      {children}
    </label>
  );
}
function Input({ testid, ...props }) {
  return (
    <input
      {...props}
      data-testid={testid}
      className="w-full bg-transparent border border-slate-200 focus:border-primary-brand outline-none px-4 py-3 text-sm text-slate-900"
    />
  );
}
function Select({ testid, children, ...props }) {
  return (
    <select
      {...props}
      data-testid={testid}
      className="w-full bg-transparent border border-slate-200 focus:border-primary-brand outline-none px-4 py-3 text-sm text-slate-900"
    >
      {children}
    </select>
  );
}
